import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';

/**
 * usePaidGate — wrap any interactive handler so unpaid users are intercepted
 * by the in-app <UpgradeModal /> instead of triggering the real action.
 *
 *   const { gate, isPaid } = usePaidGate();
 *   <Button onClick={gate(handleUpload)} />
 *
 * For an unpaid user (resolvePaidStatus === false) the wrapped function returns
 * BEFORE any side effect runs — the OS file picker never opens, the clipboard
 * is never written, and no success toast fires. Paid users run `fn` untouched,
 * so the R2 upload pipeline and every other flow behave exactly as before.
 */
export function usePaidGate() {
    const { isPaid } = useAuth();
    const { openUpgrade } = useUpgradeModal();

    const gate = useCallback(
        (fn) => (...args) => {
            if (!isPaid) {
                openUpgrade();
                return undefined;
            }
            return typeof fn === 'function' ? fn(...args) : undefined;
        },
        [isPaid, openUpgrade],
    );

    return { isPaid, gate, openUpgrade };
}
